/**
 * Force calculation weights according to design document
 */
export const FORCE_WEIGHTS = {
    ORTHOGONAL: 3, // Direct neighbors (up, down, left, right)
    DIAGONAL: 1, // Diagonal neighbors
    GLOBAL: 0.01, // Per territory owned globally
};
/**
 * ForceCalculator - Calculates force priority for trap resolution
 *
 * According to design document priority:
 * 1. Orthogonal sides (weight 3)
 * 2. Diagonal support (weight 1)
 * 3. Global board presence (tiebreaker)
 * 4. Parity results in burned territory
 */
export class ForceCalculator {
    board;
    constructor(board) {
        this.board = board;
    }
    /**
     * Calculates force for all sides surrounding a position
     */
    calculateForces(trappedPosition, excludeSide) {
        const forces = [];
        const involvedSides = new Set();
        // Collect all sides from orthogonal and diagonal neighbors
        this.collectInvolvedSides(trappedPosition, involvedSides, excludeSide);
        // Calculate force for each involved side
        for (const sideId of involvedSides) {
            const force = this.calculateSideForce(trappedPosition, sideId);
            forces.push(force);
        }
        // Sort by total force (descending)
        forces.sort((a, b) => b.totalForce - a.totalForce);
        return forces;
    }
    /**
     * Determines the winner based on CASCADING force calculation
     * ✅ ИСПРАВЛЕНО: Каскадный подсчет согласно дизайн-документу (строки 614-630)
     *
     * Алгоритм:
     * 1. Сравниваем ТОЛЬКО orthogonal force
     * 2. Если паритет → добавляем diagonal force
     * 3. Если паритет → добавляем global force
     * 4. Если ПОЛНЫЙ паритет → null (burned territory)
     *
     * Returns null if there's a FULL parity (burned territory)
     */
    determineWinner(forces) {
        if (forces.length === 0)
            return null;
        if (forces.length === 1)
            return forces[0].sideId;
        const EPSILON = 0.001; // Для сравнения float чисел
        // ============================================
        // ШАГ 1: Сравниваем ТОЛЬКО по orthogonal
        // ============================================
        // Сортируем по orthogonal force (descending)
        const sortedByOrthogonal = [...forces].sort((a, b) => b.orthogonalForce - a.orthogonalForce);
        const topOrthogonal = sortedByOrthogonal[0].orthogonalForce;
        // Находим всех с максимальным orthogonal force
        const orthogonalLeaders = sortedByOrthogonal.filter(f => Math.abs(f.orthogonalForce - topOrthogonal) < EPSILON);
        // Если один лидер → победитель найден
        if (orthogonalLeaders.length === 1) {
            return orthogonalLeaders[0].sideId;
        }
        // ============================================
        // ШАГ 2: Паритет по orthogonal → добавляем diagonal
        // ============================================
        // Сортируем лидеров по orthogonal + diagonal
        const sortedByOrthDiag = orthogonalLeaders.sort((a, b) => {
            const forceA = a.orthogonalForce + a.diagonalForce;
            const forceB = b.orthogonalForce + b.diagonalForce;
            return forceB - forceA;
        });
        const topOrthDiag = sortedByOrthDiag[0].orthogonalForce + sortedByOrthDiag[0].diagonalForce;
        // Находим всех с максимальным orthogonal + diagonal
        const orthDiagLeaders = sortedByOrthDiag.filter(f => {
            const force = f.orthogonalForce + f.diagonalForce;
            return Math.abs(force - topOrthDiag) < EPSILON;
        });
        // Если один лидер → победитель найден
        if (orthDiagLeaders.length === 1) {
            return orthDiagLeaders[0].sideId;
        }
        // ============================================
        // ШАГ 3: Паритет по orth+diag → добавляем global
        // ============================================
        // Сортируем лидеров по orthogonal + diagonal + global
        const sortedByTotal = orthDiagLeaders.sort((a, b) => {
            const forceA = a.orthogonalForce + a.diagonalForce + a.globalForce;
            const forceB = b.orthogonalForce + b.diagonalForce + b.globalForce;
            return forceB - forceA;
        });
        const topTotal = sortedByTotal[0].orthogonalForce + sortedByTotal[0].diagonalForce + sortedByTotal[0].globalForce;
        // Находим всех с максимальным total force
        const totalLeaders = sortedByTotal.filter(f => {
            const force = f.orthogonalForce + f.diagonalForce + f.globalForce;
            return Math.abs(force - topTotal) < EPSILON;
        });
        // Если один лидер → победитель найден
        if (totalLeaders.length === 1) {
            return totalLeaders[0].sideId;
        }
        // ============================================
        // ШАГ 4: ПОЛНЫЙ ПАРИТЕТ → BURNED TERRITORY
        // ============================================
        return null;
    }
    /**
     * The side among `candidates` that surrounds `positions` most strongly — who
     * takes a semi-encircled field (or group) when its owner doesn't choose in
     * time, and the AI's fallback pick. Compared in order: orthogonal contacts
     * with the field(s), then diagonal contacts, then territory on the whole map.
     * A tie on all three is settled at random among the tied sides — there is
     * always a recipient, the land is never burned here. Contacts are summed over
     * every position, so a group is judged as a whole.
     */
    strongestSurrounding(positions, candidates, random = Math.random) {
        const unique = [...new Set(candidates)];
        if (unique.length === 0)
            return null;
        let leaders = unique.map((sideId) => {
            let orthogonal = 0;
            let diagonal = 0;
            for (const position of positions) {
                for (const n of position.getOrthogonalNeighbors()) {
                    if (this.board.getTerritoryAt(n)?.owner === sideId)
                        orthogonal++;
                }
                for (const n of position.getDiagonalNeighbors()) {
                    if (this.board.getTerritoryAt(n)?.owner === sideId)
                        diagonal++;
                }
            }
            const territory = this.board.getSide(sideId)?.territories.size ?? 0;
            return { sideId, keys: [orthogonal, diagonal, territory] };
        });
        for (let k = 0; k < 3; k++) {
            const top = Math.max(...leaders.map((l) => l.keys[k]));
            leaders = leaders.filter((l) => l.keys[k] === top);
            if (leaders.length === 1)
                return leaders[0].sideId;
        }
        const i = Math.min(leaders.length - 1, Math.floor(random() * leaders.length));
        return leaders[i].sideId;
    }
    /**
     * Calculates detailed force for a specific side
     */
    calculateSideForce(position, sideId) {
        const result = {
            sideId,
            orthogonalForce: 0,
            diagonalForce: 0,
            globalForce: 0,
            totalForce: 0,
            details: {
                orthogonalPositions: [],
                diagonalPositions: [],
                totalTerritories: 0,
            },
        };
        // Calculate orthogonal force
        const orthogonalNeighbors = position.getOrthogonalNeighbors();
        for (const neighbor of orthogonalNeighbors) {
            const territory = this.board.getTerritoryAt(neighbor);
            if (territory?.owner === sideId) {
                result.orthogonalForce += FORCE_WEIGHTS.ORTHOGONAL;
                result.details.orthogonalPositions.push(neighbor);
            }
        }
        // Calculate diagonal force
        const diagonalNeighbors = position.getDiagonalNeighbors();
        for (const neighbor of diagonalNeighbors) {
            const territory = this.board.getTerritoryAt(neighbor);
            if (territory?.owner === sideId) {
                result.diagonalForce += FORCE_WEIGHTS.DIAGONAL;
                result.details.diagonalPositions.push(neighbor);
            }
        }
        // Calculate global force (tiebreaker)
        const side = this.board.getSide(sideId);
        if (side) {
            result.details.totalTerritories = side.territories.size;
            result.globalForce = side.territories.size * FORCE_WEIGHTS.GLOBAL;
        }
        // Calculate total force
        result.totalForce = result.orthogonalForce + result.diagonalForce + result.globalForce;
        return result;
    }
    /**
     * Collects all sides involved in surrounding a position.
     * Only orthogonal neighbors count as "sides" — diagonal neighbors contribute
     * force to orthogonal sides but cannot receive territory on their own.
     */
    collectInvolvedSides(position, sides, excludeSide) {
        const orthogonalNeighbors = position.getOrthogonalNeighbors();
        for (const neighbor of orthogonalNeighbors) {
            const territory = this.board.getTerritoryAt(neighbor);
            if (territory?.owner !== null &&
                territory?.owner !== undefined &&
                territory.owner !== excludeSide) {
                sides.add(territory.owner);
            }
        }
        // Diagonal neighbors are intentionally excluded: they provide diagonal force
        // support to orthogonal sides (via calculateSideForce) but do not themselves
        // qualify as surrounding sides that can receive territory.
    }
    /**
     * Provides detailed force breakdown for UI display
     */
    getForceBreakdown(position) {
        const forces = this.calculateForces(position);
        let breakdown = 'Force Calculation Breakdown:\n';
        breakdown += '============================\n';
        for (const force of forces) {
            breakdown += `\nSide ${force.sideId}:\n`;
            breakdown += `  Orthogonal (×${FORCE_WEIGHTS.ORTHOGONAL}): ${force.details.orthogonalPositions.length} positions = ${force.orthogonalForce}\n`;
            breakdown += `  Diagonal (×${FORCE_WEIGHTS.DIAGONAL}): ${force.details.diagonalPositions.length} positions = ${force.diagonalForce}\n`;
            breakdown += `  Global (×${FORCE_WEIGHTS.GLOBAL}): ${force.details.totalTerritories} territories = ${force.globalForce.toFixed(2)}\n`;
            breakdown += `  TOTAL: ${force.totalForce.toFixed(2)}\n`;
        }
        const winner = this.determineWinner(forces);
        if (winner === null) {
            breakdown += '\n⚠️ PARITY - Territory will be burned!\n';
        }
        else {
            breakdown += `\n🏆 Winner: Side ${winner}\n`;
        }
        return breakdown;
    }
}
//# sourceMappingURL=ForceCalculator.js.map